import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { AraButtonToggleGroupComponent } from './ara-button-toggle-group.component';

/**
 * araButtonToggle — one segment of an {@link AraButtonToggleGroupComponent}.
 *
 * Put it on a native `<button>` inside `<ara-button-toggle-group>`; it stays a real button
 * (a11y, keyboard, native `disabled`) and reports its selected state from the group.
 *
 * ```html
 * <button araButtonToggle value="grid"><mat-icon>grid_view</mat-icon> Grid</button>
 * ```
 */
@Component({
	// eslint-disable-next-line @angular-eslint/component-selector -- bewusst Attribut-Selektor (wie ara-link)
	selector: 'button[araButtonToggle]',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	template: '<ng-content></ng-content>',
	host: {
		class: 'ara-btn-toggle',
		type: 'button',
		'[class.ara-btn-toggle--selected]': 'selected()',
		'[class.ara-btn-toggle--disabled]': 'isDisabled()',
		'[disabled]': 'isDisabled()',
		'[attr.aria-pressed]': 'selected()',
		'(click)': 'select()',
	},
})
export class AraButtonToggleComponent {
	private readonly group = inject(AraButtonToggleGroupComponent, { optional: true });

	/** The value this segment represents. */
	readonly value = input<unknown>();
	/** Disable just this segment. */
	readonly disabled = input(false, { transform: booleanAttribute });

	protected readonly selected = computed(() => this.group?.isSelected(this.value()) ?? false);
	protected readonly isDisabled = computed(() => this.disabled() || (this.group?.disabled() ?? false));

	protected select(): void {
		this.group?.toggle(this.value());
	}
}
